import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'framer-motion';
import { ChevronDownIcon } from 'lucide-react';
interface FAQItemProps {
  question: string;
  answer: string;
  isOpen: boolean;
  onClick: () => void;
}
function FAQItem({
  question,
  answer,
  isOpen,
  onClick
}: FAQItemProps) {
  return <div className="border-b border-wheat/20 last:border-b-0" data-id="element-2">
      <button onClick={onClick} className="w-full py-6 flex items-center justify-between text-left hover:bg-wheat/5 transition-colors duration-200 px-4 rounded-lg" aria-expanded={isOpen} data-id="element-3">
        <span className="font-heading text-lg md:text-xl font-semibold text-brown pr-8" data-id="element-4">
          {question}
        </span>
        <motion.div animate={{
        rotate: isOpen ? 180 : 0
      }} transition={{
        duration: 0.3
      }} data-id="element-5">
          <ChevronDownIcon className="w-6 h-6 text-terracotta flex-shrink-0" data-id="element-6" />
        </motion.div>
      </button>
      <AnimatePresence data-id="element-7">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3
      }} className="overflow-hidden" data-id="element-8">
            <div className="pb-6 px-4" data-id="element-9">
              <p className="font-body text-base md:text-lg text-brown/80 leading-relaxed" data-id="element-10">
                {answer}
              </p>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
export function FAQSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const faqs = [{
    question: '¿Es realmente apto para celíacos?',
    answer: 'Sí, todas las recetas son 100% sin TACC. Sin embargo, es importante que sigas las buenas prácticas de cocina sin gluten en tu hogar para evitar contaminación cruzada.'
  }, {
    question: '¿Qué endulzantes se usan?',
    answer: 'Uso endulzantes que no generan picos de glucosa, como eritritol, stevia, y monk fruit. Te explico cómo usarlos correctamente para lograr el mejor sabor y textura.'
  }, {
    question: '¿Necesito ingredientes difíciles de conseguir?',
    answer: 'No. Todos los ingredientes se consiguen en dietéticas o supermercados. Te doy alternativas y marcas recomendadas para que no tengas que buscar demasiado.'
  }, {
    question: '¿Es solo para personas con restricciones?',
    answer: 'No. Si querés comer más sano, reducir harinas refinadas o azúcar, o simplemente aprender técnicas de panadería profesional, este ebook es para vos.'
  }, {
    question: '¿Necesito experiencia en panadería?',
    answer: 'No necesitás experiencia previa. Te explico todo paso a paso, desde lo más básico hasta técnicas avanzadas. Lo importante es tener ganas de aprender.'
  }, {
    question: '¿Cuánto tiempo lleva hacer el pan?',
    answer: 'Depende del tipo de pan. Algunos llevan 2-3 horas (incluyendo leudado), otros se pueden hacer en menos tiempo. Te enseño a planificar para que se adapte a tu rutina.'
  }, {
    question: '¿Qué pasa si no me sale bien?',
    answer: 'Te enseño a identificar qué salió mal y cómo corregirlo. El método incluye troubleshooting para que puedas ajustar y mejorar con cada intento.'
  }, {
    question: '¿Puedo hacer preguntas después de comprar?',
    answer: 'El ebook está diseñado para ser completo y autosuficiente. Incluye toda la información que necesitás para tener éxito desde el primer intento.'
  }];
  return <section ref={ref} className="w-full bg-wheat/10 px-4 py-16 md:py-24" data-id="element-11">
      <div className="max-w-4xl mx-auto" data-id="element-12">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 30
      }} transition={{
        duration: 0.8
      }} className="text-center mb-12" data-id="element-13">
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-brown mb-6" data-id="element-14">
            Preguntas Frecuentes
          </h2>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 30
      }} transition={{
        duration: 0.8,
        delay: 0.2
      }} className="bg-white rounded-2xl shadow-lg p-6 md:p-8" data-id="element-15">
          {faqs.map((faq, index) => <FAQItem key={index} question={faq.question} answer={faq.answer} isOpen={openIndex === index} onClick={() => setOpenIndex(openIndex === index ? null : index)} data-id="element-16" />)}
        </motion.div>
      </div>
    </section>;
}